import * as React from 'react';
import {isChild as userIsChild} from '@practicegenius/domain/src/data/users/birthdate';
import {
  calculateProgress,
  PrimaryActionButtonProps,
  primaryActionButtonProps,
  SecondaryActionButtonProps,
  secondaryActionButtonProps,
} from './utils';
import * as registrationMachine from '../machines/accountRegistration';
import lang from '../lang';
import {UseMachineType} from '../machines/useAccountRegistrationMachineType';

type Props = {
  registrationMachine: UseMachineType;
};

type ScreenProps = Props & {
  lang: typeof lang.registerEmail;
  onFormSubmit: React.ComponentProps<'form'>['onSubmit'];
  progress: number;
  actionButtons: {
    primary: PrimaryActionButtonProps;
    secondary: SecondaryActionButtonProps;
  };
  emailInputProps: {
    name: 'responsibleEmail' | 'email';
    value: string | null;
    onChange: (field: 'responsibleEmail' | 'email', value: string) => unknown;
    onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
    error: string | undefined;
  };
  setUseResponsibleEmail: (a: boolean) => void;
  birthDate: number | Date | null;
  firstName: string | null;
  onEmailSwap: () => void;
};

export type RegisterEmailScreenType = React.ComponentType<ScreenProps>;

export const RegisterEmail = (
  props: Props & {children: RegisterEmailScreenType}
) => {
  const [
    {
      context: {
        navigator,
        account,
        practiceUserRecord,
        confirmedEmail,
        validationErrors,
        inviteCode,
      },
      matches,
    },
    send,
  ] = props.registrationMachine;

  const {birthdate, firstname} = account;
  const isChild = birthdate ? userIsChild(birthdate as Date) : false;
  const [useResponsibleEmail, setUseResponsibleEmail] = React.useState(isChild);

  const activeEmailField = useResponsibleEmail ? 'responsibleEmail' : 'email';
  const inactiveEmailField =
    activeEmailField === 'email' ? 'responsibleEmail' : 'email';

  const progress = calculateProgress({
    draftAccount: account,
    location: navigator.location,
  });

  /**
   * Only prefill email from practice user if the registration was booted from an invite
   * @see https://practicegenius.atlassian.net/browse/PAT-15166
   */
  const practiceUserEmail = inviteCode ? practiceUserRecord.email : null;
  const email = account[activeEmailField] ?? practiceUserEmail;

  const actionButtons = {
    primary: primaryActionButtonProps({
      children: lang.registerEmail.buttons.primary.title,
      loading: matches({
        [registrationMachine.RegistrationState.email]:
          registrationMachine.RegistrationState.submittingEmail,
      }),
      disabled: Boolean(
        !email || validationErrors?.account?.[activeEmailField]
      ),
    }),
    secondary: secondaryActionButtonProps({
      children: lang.registerEmail.buttons.secondary.title,
      onClick: () => navigator.goBack(),
      loading: false,
      disabled: false,
    }),
  };

  const renderProps: ScreenProps = {
    ...props,
    lang: lang.registerEmail,
    onFormSubmit: e => {
      e.preventDefault();
      // Skip the ConfirmEmail screen if email is already confirmed
      if (account[activeEmailField] === confirmedEmail[activeEmailField]) {
        send(registrationMachine.RegistrationEvent.SKIP_TO_EMAIL_CONFIRMED);
        return;
      }
      send({
        type: registrationMachine.RegistrationEvent.UPDATE_REGISTRATION,
        payload: {
          [activeEmailField]: email,
          [inactiveEmailField]: null,
          useResponsibleEmail,
        },
      });
    },
    progress,
    actionButtons,
    emailInputProps: {
      name: activeEmailField,
      value: email,
      onChange: (field, value) => {
        send({
          type: registrationMachine.RegistrationEvent.UPDATE_DATA,
          payload: {account: {[field]: value}},
        });
      },
      onBlur: ({relatedTarget}) => {
        if ((relatedTarget as HTMLElement)?.getAttribute('type') === 'button') {
          return null;
        }
        if (!email) {
          send({
            type: registrationMachine.RegistrationEvent.HAS_VALIDATION_ERRORS,
            errors: {
              account: {
                [activeEmailField]: lang.registerEmail.emailInput.errors.blank,
              },
            },
          });
        }
      },
      error: validationErrors?.account?.[activeEmailField],
    },
    setUseResponsibleEmail,
    birthDate: birthdate,
    firstName: firstname,
    onEmailSwap: () => {
      send({
        type: registrationMachine.RegistrationEvent.UPDATE_DATA,
        payload: {
          account: {
            responsibleEmail: email,
          },
        },
      });
    },
  };

  const Screen = props.children;
  return <Screen {...renderProps} />;
};
